import React from 'react';

// ─── Helpers ─────────────────────────────────────────────────────────

function fmtNum(v: unknown): string {
  const n = Number(v);
  if (isNaN(n)) return String(v);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
  if (abs >= 1_000) return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
  return String(n);
}

// ─── Eval Data Styled View ──────────────────────────────────────────

export function EvalDataView({ data, verdict, ruleType }: { data: Record<string, any>; verdict: string; ruleType?: string }) {
  const [expandedMonths, setExpandedMonths] = React.useState<Set<number>>(new Set());
  const [showRawJson, setShowRawJson] = React.useState(false);
  const [showTargets, setShowTargets] = React.useState(false);

  const condition = data?.condition as Record<string, any> | undefined;
  const positions = data?.positions as { monthCount?: number; months?: any[] } | undefined;
  const months = positions?.months;
  const monthsAhead = condition?.months_ahead ?? 12;
  const targetPercent = condition?.target_percent ?? condition?.min_coverage_percent;
  const perMonthValues: number[] = Array.isArray(condition?.values) ? condition.values : [];

  // Rule types that are NOT month-projection based
  const NON_MONTH_RULES = new Set(['exposure_threshold', 'price_target', 'volatility_spike', 'backwardation', 'cost_basis_drift', 'm2m_pnl']);
  const isMonthBased = !NON_MONTH_RULES.has(ruleType || '') && months && Array.isArray(months) && months.length > 0;

  // Determine pass/fail per month based on rule type
  const getMonthStatus = (m: any, idx: number): 'pass' | 'fail' | 'skipped' => {
    if (idx >= monthsAhead) return 'skipped';
    const pct = m.coveragePct;
    if (pct == null) return 'pass';
    const target = perMonthValues[idx] || targetPercent || 100;
    return pct >= target ? 'pass' : 'fail';
  };

  const toggleMonth = (idx: number) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  // Condition fields to display (exclude arrays and positions)
  const conditionEntries = condition
    ? Object.entries(condition).filter(([k, v]) => k !== 'values' && !Array.isArray(v))
    : [];

  // Extra data fields (breach info from evaluators)
  const extraKeys = Object.keys(data).filter(k => k !== 'condition' && k !== 'positions');

  if (!isMonthBased) {
    // Exposure threshold: show visual bar gauge
    if (ruleType === 'exposure_threshold' && typeof data.value === 'number') {
      const val = data.value as number;
      const dangerBelow = Number(data.dangerBelow) || 0;
      const warnBelow = Number(data.warnBelow) || 0;
      const warnAbove = Number(data.warnAbove) || 0;
      const dangerAbove = Number(data.dangerAbove) || 0;
      const zone = data.zone as string || '';
      const snapshotDate = data.snapshotDate ? new Date(data.snapshotDate).toLocaleDateString() : '';

      // Ordered pills: dangerBelow, warnBelow, value, warnAbove, dangerAbove
      const pills: { label: string; value: number; color: string; bg: string; border: string; isCurrent?: boolean }[] = [
        { label: 'DANGER', value: dangerBelow, color: '#991b1b', bg: '#fef2f2', border: '#fecaca' },
        { label: 'WARN', value: warnBelow, color: '#9a3412', bg: '#fff7ed', border: '#fed7aa' },
        { label: 'WARN', value: warnAbove, color: '#9a3412', bg: '#fff7ed', border: '#fed7aa' },
        { label: 'DANGER', value: dangerAbove, color: '#991b1b', bg: '#fef2f2', border: '#fecaca' },
      ];

      // Determine color for the actual value
      let valColor = '#166534'; let valBg = '#f0fdf4'; let valBorder = '#86efac';
      if (zone.includes('danger')) { valColor = '#991b1b'; valBg = '#fef2f2'; valBorder = '#fca5a5'; }
      else if (zone.includes('warning')) { valColor = '#9a3412'; valBg = '#fff7ed'; valBorder = '#fdba74'; }

      // Insert the value pill in sorted order
      const allPills = [...pills, { label: 'VALUE', value: val, color: valColor, bg: valBg, border: valBorder, isCurrent: true }]
        .sort((a, b) => a.value - b.value);

      const fmtVal = (v: number) => Math.abs(v) >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v.toLocaleString(undefined, { maximumFractionDigits: 0 });

      return (
        <div className="space-y-1.5">
          {/* Single-row gauge */}
          <div className="flex items-center gap-1.5">
            {allPills.map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono whitespace-nowrap"
                style={{
                  backgroundColor: p.bg,
                  color: p.color,
                  border: `1.5px solid ${p.border}`,
                  fontWeight: p.isCurrent ? 700 : 500,
                  boxShadow: p.isCurrent ? `0 0 0 2px ${p.border}` : undefined,
                }}
              >
                <span className="opacity-60 text-[8px] font-semibold">{p.label}</span>
                <span>{fmtVal(p.value)}</span>
              </div>
            ))}
            {snapshotDate && <span className="text-[9px] text-gray-400 ml-2">{snapshotDate}</span>}
          </div>
          {/* Toggle raw JSON */}
          <div className="flex justify-end">
            <button
              className="text-[10px] text-gray-400 hover:text-blue-500 underline"
              onClick={(e) => { e.stopPropagation(); setShowRawJson(!showRawJson); }}
            >
              {showRawJson ? 'Hide' : 'Show'} raw JSON
            </button>
          </div>
          {showRawJson && (
            <pre className="text-[10px] leading-relaxed font-mono text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto bg-white rounded p-3 border border-gray-200">
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </div>
      );
    }

    // Price target: two pills (spot vs target) with arrow
    if (ruleType === 'price_target' && (data.spotPrice || data.target)) {
      const spot = Number(data.spotPrice) || 0;
      const target = Number(data.target) || 0;
      const currency = data.currency || '';
      const direction = data.direction || 'above';
      const triggered = (direction === 'above' && spot >= target) || (direction === 'below' && spot <= target);
      const fmtP = (v: number) => v >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v.toFixed(2);

      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono"
              style={{ backgroundColor: '#f0fdf4', color: '#166534', border: '1.5px solid #86efac' }}>
              <span className="opacity-60 text-[8px] font-semibold">TARGET</span>
              <span>{currency} {fmtP(target)}</span>
            </div>
            <span className="text-gray-400 text-[10px]">{direction === 'above' ? '≤' : '≥'}</span>
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono"
              style={{
                backgroundColor: triggered ? '#fef2f2' : '#f0fdf4',
                color: triggered ? '#991b1b' : '#166534',
                border: `1.5px solid ${triggered ? '#fca5a5' : '#86efac'}`,
                fontWeight: 700,
                boxShadow: `0 0 0 2px ${triggered ? '#fca5a5' : '#86efac'}`,
              }}>
              <span className="opacity-60 text-[8px] font-semibold">SPOT</span>
              <span>{currency} {fmtP(spot)}</span>
            </div>
          </div>
          <div className="flex justify-end">
            <button className="text-[10px] text-gray-400 hover:text-blue-500 underline"
              onClick={(e) => { e.stopPropagation(); setShowRawJson(!showRawJson); }}>
              {showRawJson ? 'Hide' : 'Show'} raw JSON
            </button>
          </div>
          {showRawJson && (
            <pre className="text-[10px] leading-relaxed font-mono text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto bg-white rounded p-3 border border-gray-200">
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </div>
      );
    }

    // Cost basis drift: avg cost vs spot with drift % badge + breakdown
    if (ruleType === 'cost_basis_drift' && (data.avgCost || data.spotPrice)) {
      const avgCost = Number(data.avgCost) || 0;
      const spot = Number(data.spotPrice) || 0;
      const drift = Number(data.driftPct) || 0;
      const absDrift = Math.abs(drift);
      const currency = data.baseCurrency || 'USD';
      const totalQty = Number(data.totalQty) || 0;
      const driftLabel = drift > 0 ? 'above' : 'below';
      const isDanger = absDrift > 20;
      const fmtP = (v: number) => v >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : v.toFixed(2);
      const breakdown = data.breakdown as { onHand?: { qty: number; avgPrice: number; count: number }; purchaseOrders?: { qty: number; avgPrice: number; count: number }; hedges?: { qty: number; avgPrice: number; count: number } } | undefined;

      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Avg cost pill */}
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono"
              style={{
                backgroundColor: drift > 0 ? '#fef2f2' : '#eff6ff',
                color: drift > 0 ? '#991b1b' : '#1e40af',
                border: `1.5px solid ${drift > 0 ? '#fca5a5' : '#93c5fd'}`,
                fontWeight: 700,
                boxShadow: `0 0 0 2px ${drift > 0 ? '#fca5a5' : '#93c5fd'}`,
              }}>
              <span className="opacity-60 text-[8px] font-semibold">AVG COST</span>
              <span>{currency} {fmtP(avgCost)}</span>
            </div>
            {/* Drift badge */}
            <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
              style={{
                backgroundColor: isDanger ? '#fecaca' : '#fed7aa',
                color: isDanger ? '#991b1b' : '#9a3412',
              }}>
              {drift > 0 ? '↑' : '↓'} {absDrift.toFixed(1)}% {driftLabel}
            </div>
            {/* Spot pill */}
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono"
              style={{ backgroundColor: '#f8fafc', color: '#475569', border: '1.5px solid #e2e8f0' }}>
              <span className="opacity-60 text-[8px] font-semibold">SPOT</span>
              <span>{currency} {fmtP(spot)}</span>
            </div>
            {/* Position breakdown pills */}
            {breakdown ? (
              <>
                {breakdown.onHand && breakdown.onHand.qty > 0 && (
                  <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium"
                    style={{ backgroundColor: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>
                    On-hand: {breakdown.onHand.qty.toLocaleString()} MT @ {currency} {fmtP(breakdown.onHand.avgPrice)}
                  </div>
                )}
                {breakdown.purchaseOrders && breakdown.purchaseOrders.qty > 0 && (
                  <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium"
                    style={{ backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}>
                    POs: {breakdown.purchaseOrders.qty.toLocaleString()} MT @ {currency} {fmtP(breakdown.purchaseOrders.avgPrice)}
                  </div>
                )}
                {breakdown.hedges && breakdown.hedges.qty > 0 && (
                  <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium"
                    style={{ backgroundColor: '#f5f3ff', color: '#5b21b6', border: '1px solid #ddd6fe' }}>
                    Hedges: {breakdown.hedges.qty.toLocaleString()} MT @ {currency} {fmtP(breakdown.hedges.avgPrice)}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium"
                style={{ backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>
                {totalQty.toLocaleString()} MT
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <button className="text-[10px] text-gray-400 hover:text-blue-500 underline"
              onClick={(e) => { e.stopPropagation(); setShowRawJson(!showRawJson); }}>
              {showRawJson ? 'Hide' : 'Show'} raw JSON
            </button>
          </div>
          {showRawJson && (
            <pre className="text-[10px] leading-relaxed font-mono text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto bg-white rounded p-3 border border-gray-200">
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </div>
      );
    }

    // Generic non-month rule: condition pills + key fields
    const displayKeys = Object.keys(data).filter(k => k !== 'condition' && k !== 'positions' && k !== 'ruleType');
    return (
      <div className="space-y-2">
        {condition && (
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            {Object.entries(condition).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-mono text-[10px]">
                {k}: {String(v)}
              </span>
            ))}
          </div>
        )}
        {displayKeys.length > 0 && (
          <div className="flex flex-wrap gap-1.5 text-[11px]">
            {displayKeys.map(k => {
              const v = data[k];
              const formatted = typeof v === 'number'
                ? (Math.abs(v) >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : String(v))
                : String(v);
              const isNegative = typeof v === 'number' && v < 0;
              return (
                <span key={k} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono ${isNegative ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-700'}`}>
                  <span className="text-gray-400">{k.replace(/([A-Z])/g, ' $1').trim()}:</span> {formatted}
                </span>
              );
            })}
          </div>
        )}
        <div className="flex justify-end">
          <button
            className="text-[10px] text-gray-400 hover:text-blue-500 underline"
            onClick={(e) => { e.stopPropagation(); setShowRawJson(!showRawJson); }}
          >
            {showRawJson ? 'Hide' : 'Show'} raw JSON
          </button>
        </div>
        {showRawJson && (
          <pre className="text-[10px] leading-relaxed font-mono text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto bg-white rounded p-3 border border-gray-200">
            {JSON.stringify(data, null, 2)}
          </pre>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Condition summary — months_ahead first */}
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold"
          style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}
        >
          Checking {monthsAhead} of {positions?.monthCount ?? months!.length} months
        </span>
        {targetPercent != null && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">
            Target: {targetPercent}%
          </span>
        )}
        {conditionEntries
          .filter(([k]) => k !== 'months_ahead' && k !== 'target_percent')
          .map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-mono text-[10px]">
              {k}: {typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v)}
            </span>
          ))}
        {perMonthValues.length > 0 && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] cursor-pointer hover:bg-gray-200 transition-colors"
            onClick={(e) => { e.stopPropagation(); setShowTargets(!showTargets); }}
          >
            Per-month targets [{perMonthValues.length}] <span className="text-gray-400 text-[9px]">{showTargets ? '▼' : '▶'}</span>
          </span>
        )}
      </div>

      {/* Expanded per-month targets grid */}
      {showTargets && perMonthValues.length > 0 && (
        <div className="flex flex-wrap gap-1 px-1">
          {perMonthValues.map((v, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono"
              style={{
                backgroundColor: i < monthsAhead ? '#f0f9ff' : '#f9fafb',
                color: i < monthsAhead ? '#1e40af' : '#9ca3af',
                border: `1px solid ${i < monthsAhead ? '#bfdbfe' : '#e5e7eb'}`,
              }}
            >
              <span className="font-semibold">M{i + 1}:</span> {v}%
            </span>
          ))}
        </div>
      )}

      {/* Extra evaluator data (breachMonths, shortfallMonths, etc.) */}
      {extraKeys.length > 0 && (
        <div className="text-[10px] text-gray-500 font-mono px-1">
          {extraKeys.map(k => {
            const v = data[k];
            if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string') {
              return <div key={k}><span className="font-semibold text-gray-600">{k}:</span> {v.join(', ')}</div>;
            }
            if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') {
              return (
                <div key={k}>
                  <span className="font-semibold text-gray-600">{k}:</span>{' '}
                  {v.slice(0, 4).map((item, j) => {
                    const parts = Object.entries(item).map(([ik, iv]) => `${ik}=${iv}`).join(' ');
                    return <span key={j} className="inline-block mr-2">[{parts}]</span>;
                  })}
                  {v.length > 4 && <span>+{v.length - 4} more</span>}
                </div>
              );
            }
            return null;
          })}
        </div>
      )}

      {/* Month cards */}
      <div className="grid gap-0.5">
        {months!.map((m: any, idx: number) => {
          const status = getMonthStatus(m, idx);
          const isOpen = expandedMonths.has(idx);
          const bgColor = status === 'skipped' ? '#f9fafb' : status === 'pass' ? '#f0fdf4' : '#fef2f2';
          const borderColor = status === 'skipped' ? '#e5e7eb' : status === 'pass' ? '#86efac' : '#fca5a5';
          const icon = status === 'skipped' ? '—' : status === 'pass' ? '✓' : '✗';
          const iconColor = status === 'skipped' ? '#9ca3af' : status === 'pass' ? '#16a34a' : '#dc2626';
          const target = perMonthValues[idx] || targetPercent || 100;

          return (
            <div key={idx} style={{ backgroundColor: bgColor, borderLeft: `3px solid ${borderColor}` }} className="rounded-r">
              <div
                className="flex items-center gap-2 px-2 py-1 cursor-pointer text-[11px] select-none"
                onClick={() => toggleMonth(idx)}
              >
                <span style={{ color: iconColor, fontWeight: 700, fontSize: 13, width: 16, textAlign: 'center' }}>{icon}</span>
                <span className="font-semibold text-gray-800 w-12">{m.month}</span>
                {m.coveragePct != null && (
                  <span className="font-mono text-[10px]" style={{ color: iconColor }}>
                    {m.coveragePct}%
                    {status !== 'skipped' && <span className="text-gray-400"> / {target}%</span>}
                  </span>
                )}
                {status === 'skipped' && <span className="text-[10px] text-gray-400 italic">outside check window</span>}
                <span className="ml-auto text-gray-400 text-[10px]">
                  D:{fmtNum(m.demand)}{m.buffer > 0 && <span> B:{fmtNum(m.buffer)}</span>} S:{fmtNum(m.rollingStock)}{m.pos > 0 && <span> PO:{fmtNum(m.pos)}</span>}{m.hedges > 0 && <span> H:{fmtNum(m.hedges)}</span>}
                </span>
                <span className="text-gray-300 text-[9px]">{isOpen ? '▼' : '▶'}</span>
              </div>
              {isOpen && (
                <div className="px-8 pb-1.5 text-[10px] text-gray-600 font-mono grid grid-cols-2 gap-x-4 gap-y-0.5" style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                  <span>On-hand: {fmtNum(m.onHand)} MT</span>
                  <span>Rolling stock: {fmtNum(m.rollingStock)} MT</span>
                  <span>POs: {fmtNum(m.pos)} MT</span>
                  <span>Demand: {fmtNum(m.demand)} MT</span>
                  <span>Hedges: {fmtNum(m.hedges)} MT</span>
                  {m.buffer > 0 && <span>Buffer: {fmtNum(m.buffer)} MT</span>}
                  <span>End of month: {fmtNum(m.endOfMonth)} MT</span>
                  {m.coveragePct != null && <span>Coverage: {m.coveragePct}%</span>}
                  {status !== 'skipped' && <span>Target: {target}%</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Toggle raw JSON */}
      <div className="flex justify-end">
        <button
          className="text-[10px] text-gray-400 hover:text-blue-500 underline"
          onClick={(e) => { e.stopPropagation(); setShowRawJson(!showRawJson); }}
        >
          {showRawJson ? 'Hide' : 'Show'} raw JSON
        </button>
      </div>
      {showRawJson && (
        <pre className="text-[10px] leading-relaxed font-mono text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto bg-white rounded p-3 border border-gray-200">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}
